import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { doc, getDoc,  collection,  addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../../config/firebase';
import { useAuth } from '../../../context/AuthContext';
import { uploadFile } from '../../../services/storage';
import Toast from '../../../components/common/Toast';
import FileViewer from '../../../components/common/FileViewer';
import {FaSave, FaArrowLeft, FaExclamationCircle, FaCloudUploadAlt, FaFile,} from 'react-icons/fa';

const categories = [
  { id: 'power-platform', name: 'Power Platform' },
  { id: 'sharepoint', name: 'SharePoint' },
  { id: 'office-betikleri', name: 'Office Betikleri' },
  { id: 'power-query', name: 'Power Query' },
  { id: 'vba-makrolar', name: 'VBA & Makrolar' },
  { id: 'veri-otomasyonu', name: 'Veri Otomasyonu' },
  { id: 'sablonlar', name: 'Şablonlar' },
  { id: 'entegrasyonlar', name: 'Entegrasyonlar' }
];

function EditorAutomationForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [toast, setToast] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    summary: '',
    category: '',
    tags: '',
    files: [],
    status: 'draft',
    publishedBy: user?.role,
    authorId: user?.uid
  });
  const [files, setFiles] = useState([]);

  const fetchAutomation = useCallback(async () => {
    try {
      const docRef = doc(db, 'automations', id);
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        const data = docSnap.data();
        if (user.role === 'editor' && data.authorId !== user.uid) {
          setError('Bu otomasyonu düzenleme yetkiniz yok');
          navigate('/editor/automations');
          return;
        }
        setFormData({
          title: data.title || '',
          content: data.content || '',
          summary: data.summary || '',
          category: data.category || '',
          tags: Array.isArray(data.tags) ? data.tags.join(', ') : '',
          files: data.files || [],
          status: data.status || 'draft',
          publishedBy: data.publishedBy || user.role,
          authorId: data.authorId
        });
        if (data.files && data.files.length > 0) {
          setFiles(data.files);
        }
      }
    } catch (err) {
      setError('Otomasyon yüklenirken hata oluştu');
      console.error(err);
    }
  }, [id, user, navigate]);

  useEffect(() => {
    setFormData(prev => ({
      ...prev,
      publishedBy: user?.role || '',
      authorId: user?.uid || '',
      author: user?.name || user?.email || '',
      category: user?.category || ''
    }));

    if (id) {
      fetchAutomation();
    }
  }, [id, user, fetchAutomation]);

  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files);
    
    const maxSize = 5 * 1024 * 1024;
    const invalidFiles = selectedFiles.filter(file => file.size > maxSize);
    const warningFiles = selectedFiles.filter(file => file.size > maxSize / 2);
    
    if (invalidFiles.length > 0) {
      setToast({
        message: '5MB\'dan büyük dosyalar yüklenemez: ' + invalidFiles.map(f => f.name).join(', '),
        type: 'error'
      });
      return;
    }

    if (warningFiles.length > 0) {
      setToast({
        message: 'Bazı dosyalar 2.5MB\'dan büyük, yükleme yavaş olabilir: ' + warningFiles.map(f => f.name).join(', '),
        type: 'warning'
      });
    }

    setFiles(prevFiles => [...prevFiles, ...selectedFiles]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    if (!formData.category) {
      setError('Lütfen bir kategori seçin');
      setLoading(false);
      return;
    }

    try {
      let uploadedFiles = [];
      if (files.length > 0) {
        try {
          uploadedFiles = await Promise.all(
            files.map(async (file) => {
              if (file instanceof File) {
                const processedFile = await uploadFile(file);
                return {
                  name: processedFile.name,
                  size: processedFile.size,
                  type: processedFile.type,
                  chunks: processedFile.chunks,
                  chunkCount: processedFile.chunkCount,
                  uploadedAt: processedFile.uploadedAt || serverTimestamp()
                };
              }
              return file;
            })
          );
        } catch (uploadError) {
          console.error('Dosya işleme hatası:', uploadError);
          setError('Dosya işleme sırasında bir hata oluştu');
          setLoading(false);
          return;
        }
      }

      uploadedFiles = uploadedFiles.filter(file => file !== null);

      const automationData = {
        title: formData.title.trim(),
        summary: formData.summary.trim(),
        category: formData.category,
        tags: formData.tags ? formData.tags.split(',').map(tag => tag.trim()).filter(tag => tag) : [],
        files: uploadedFiles,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        author: user?.name || user?.email || '',
        authorId: user?.uid || '',
        publishedBy: user?.role || '',
        status: 'pending'
      };

      Object.keys(automationData).forEach(key => {
        if (automationData[key] === undefined) {
          delete automationData[key];
        }
      });

      await addDoc(collection(db, 'automations'), automationData);

      setToast({
        message: 'Otomasyon başarıyla oluşturuldu',
        type: 'success'
      });

      setTimeout(() => {
        navigate('/editor/automations');
      }, 1500);
    } catch (err) {
      console.error('Genel hata:', err);
      setError(err.message || 'İşlem sırasında bir hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const handleFileDelete = (file) => {
    setFiles(prevFiles => prevFiles.filter(f => f.name !== file.name));
  };

  return (
    <>
      <div className="max-w-5xl mx-auto py-8 px-4">
        <div className="flex items-center mb-8">
          <button
            onClick={() => navigate(-1)}
            className="mr-4 text-gray-600 hover:text-gray-800 transition-colors"
          >
            <FaArrowLeft size={24} />
          </button>
          <h1 className="text-3xl font-bold text-gray-900">
            {id ? 'Otomasyon Düzenle' : 'Yeni Otomasyon'}
          </h1>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-red-100 border-l-4 border-red-500 text-red-700">
            <div className="flex items-center">
              <FaExclamationCircle className="mr-2" />
              <p>{error}</p>
            </div>
          </div>
        )}

        <div className="space-y-6">
          {/* Başlık */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Başlık
            </label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              className="w-full px-4 py-3 rounded-xl bg-white border border-gray-200 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
              placeholder="Otomasyon başlığı"
              required
            />
          </div>

          {/* Özet */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Özet
            </label>
            <textarea
              value={formData.summary}
              onChange={(e) => setFormData({ ...formData, summary: e.target.value })}
              className="w-full px-4 py-3 rounded-xl bg-white border border-gray-200 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
              rows={3}
              placeholder="Otomasyon hakkında kısa bir özet"
            />
          </div>

          {/* Kategoriler */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Kategori
            </label>
            <select
              value={formData.category}
              onChange={(e) => setFormData({ ...formData, category: e.target.value })}
              className="w-full px-4 py-3 rounded-xl bg-white border border-gray-200 focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all duration-200"
              required
            >
              <option value="">Kategori Seçin</option>
              {categories.map(category => (
                <option key={category.id} value={category.id}>
                  {category.name}
                </option>
              ))}
            </select>
          </div>

          {/* Etiketler */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Etiketler
            </label>
            <input
              type="text"
              value={formData.tags}
              onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
              className="w-full px-4 py-3 rounded-xl bg-white border border-gray-200 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
              placeholder="Etiketleri virgülle ayırın (örn: power automate, sharepoint, excel)"
            />
          </div>

          {/* Dosya Yükleme */}
          <div className="bg-white/80 backdrop-blur-lg rounded-2xl shadow-lg p-8 border border-white/20">
            <h2 className="text-2xl font-semibold text-gray-900 mb-6 flex items-center">
              <FaFile className="mr-3 text-blue-500" />
              Dosyalar
            </h2>

            <div className="flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-xl hover:border-blue-500 transition-all duration-200">
              <div className="space-y-1 text-center">
                <FaCloudUploadAlt className="mx-auto h-12 w-12 text-gray-400" />
                <div className="flex text-sm text-gray-600">
                  <label className="relative cursor-pointer bg-white rounded-md font-medium text-blue-600 hover:text-blue-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-blue-500">
                    <span>Dosya Yükle</span>
                    <input
                      type="file"
                      className="sr-only"
                      multiple
                      onChange={handleFileChange}
                    />
                  </label>
                  <p className="pl-1">veya sürükleyip bırakın</p>
                </div>
                <p className="text-xs text-gray-500">Tüm dosya türleri, max 5MB</p>
              </div>
            </div>

            {files.length > 0 && (
              <div className="mt-6">
                <FileViewer files={files} onDelete={handleFileDelete} />
              </div>
            )}
          </div>

          {/* Kaydet Butonu */}
          <div className="flex justify-end mt-8">
            <button
              type="button"
              onClick={handleSubmit}
              disabled={loading}
              className={`flex items-center px-6 py-3 bg-gradient-to-r from-blue-500 to-indigo-600 text-white rounded-xl font-medium shadow-lg hover:shadow-xl transition-all duration-200 ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              <FaSave className="mr-2" />
              {loading ? 'Kaydediliyor...' : 'Kaydet'}
            </button>
          </div>
        </div>
      </div>
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
    </>
  );
}

export default EditorAutomationForm; 